import { useMemo, useState } from 'react';
import { DsButton, DsModal, DsSearchInput } from '@/modules/internal-v2/ds';
import { useGetAllItems } from '@/api/items/items.hooks';
import type { Item } from '@/api/items/items.types.ts';
import { compareByItemGroupThenName } from '@/utils/item-catalog-sort';
import { formatEur } from '../email-builder.constants';

interface ItemPickerModalProps {
  open: boolean;
  /**
   * When true, picking a row keeps the modal open so several items can be
   * added in a row; the user closes it via the footer button. When false
   * (default), picking a row closes the modal immediately.
   */
  multiple?: boolean;
  onClose: () => void;
  onPick: (item: Item) => void;
}

const MAX_RESULTS = 60;

/**
 * Modal that searches the SAP item catalog and returns the picked item.
 *
 * The component stays mounted and only toggles visibility via `open`, so the
 * search term survives a close/reopen. The (potentially large) catalog fetch
 * is deferred until the modal is opened the first time, then cached. Results
 * use the same order as the external catalog page (`compareByItemGroupThenName`).
 */
export function ItemPickerModal({
  open,
  multiple = false,
  onClose,
  onPick,
}: ItemPickerModalProps) {
  const [search, setSearch] = useState('');

  // Defer the catalog fetch until the modal is first opened; once activated it
  // stays cached so reopening is instant. Latching it on the first open is the
  // React "adjust state when a prop changes" pattern (guarded render setState).
  const [activated, setActivated] = useState(false);
  if (open && !activated) setActivated(true);

  const {
    data: items,
    isPending,
    isError,
  } = useGetAllItems(undefined, {
    // queryKey is set by the hook — repeated here only to satisfy the typed
    // options signature while adding `enabled`.
    queryKey: ['items', undefined],
    enabled: activated,
  });

  // Sort the whole catalog once per load, reusing the external catalog order.
  const sorted = useMemo(
    () => [...(items ?? [])].sort(compareByItemGroupThenName),
    [items],
  );

  // Precompute one lowercase haystack per item so a keystroke doesn't
  // re-allocate a string for every catalog entry.
  const haystacks = useMemo(
    () =>
      sorted.map((item) =>
        `${item.name} ${item.id} ${item.manufacturer}`.toLowerCase(),
      ),
    [sorted],
  );

  const results = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return sorted.slice(0, MAX_RESULTS);
    const matches: Item[] = [];
    for (let i = 0; i < sorted.length && matches.length < MAX_RESULTS; i++) {
      if (haystacks[i].includes(query)) matches.push(sorted[i]);
    }
    return matches;
  }, [sorted, haystacks, search]);

  const handlePick = (item: Item) => {
    onPick(item);
    // Single-select: pick is the final action, so close. Multi-select: stay
    // open so further items can be added.
    if (!multiple) onClose();
  };

  return (
    <DsModal
      open={open}
      onClose={onClose}
      title="Artikel auswählen"
      description={
        multiple
          ? 'Mehrere Artikel aus dem SAP-Katalog auswählen'
          : 'Artikel aus dem SAP-Katalog suchen und übernehmen'
      }
      footer={
        multiple ? (
          <DsButton
            type="button"
            variant="primary"
            size="small"
            onClick={onClose}
          >
            Fertig
          </DsButton>
        ) : undefined
      }
    >
      <div className="eb-picker">
        <DsSearchInput
          value={search}
          onSearch={setSearch}
          debounce={150}
          placeholder="Name, Artikelnummer oder Hersteller…"
          autoFocus
        />

        {isPending && (
          <div className="eb-picker-state">Artikel werden geladen…</div>
        )}
        {isError && (
          <div className="eb-picker-state eb-picker-error">
            Artikel konnten nicht geladen werden.
          </div>
        )}

        {!isPending && !isError && (
          <div className="eb-picker-list ds-scroll">
            {results.length === 0 && (
              <div className="eb-picker-state">Keine Treffer.</div>
            )}
            {results.map((item) => (
              <button
                type="button"
                key={item.id}
                className="eb-picker-row"
                onClick={() => handlePick(item)}
              >
                <span className="eb-picker-thumb">
                  {item.imageUrl ? (
                    <img src={item.imageUrl} alt="" loading="lazy" />
                  ) : (
                    <span className="eb-picker-thumb-empty">—</span>
                  )}
                </span>
                <span className="eb-picker-info">
                  <span className="eb-picker-name">{item.name}</span>
                  <span className="eb-picker-meta">
                    {item.id} · {item.manufacturer}
                  </span>
                </span>
                <span className="eb-picker-price">
                  {item.itemPrice ? formatEur(item.itemPrice) : '—'}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </DsModal>
  );
}
